import React, { useState, useEffect, useContext } from "react";
import { getRequest, postRequest } from "../../../helpers/ApiHelpers";
import { css } from "emotion";
import {
  Button,
  Header,
  Grid,
  Form,
  Checkbox
} from "semantic-ui-react";
import RichTextEditor from "../../RichTextEditor";
import { withRouter } from "react-router-dom";
import { StateContext } from "../../State";
import { readFileAsBase64 } from "../../../helpers/ReadFileAsBase64";
import FileUpload from "../../FileUpload";
import shortid from "shortid";
import { logEvent } from "../../../helpers/analyticsLogger";

const defaultWithholdingTax = {
  isApplicable: null
};

export function EditWithholdingTax({ match, history }) {
  const { selectedState } = useContext(StateContext);
  const [state, setState] = useState({
    submitAttempted: false,
    submitting: false,
    withholdingTax: defaultWithholdingTax,
    files: [],
    filesToDelete: []
  });

  async function getWitholdingTaxByState(stateId) {
    const result = await getRequest(`/tax/getWithholdingTaxByState`, {
      stateId
    });
    if (result.success) {
      const data = result.data;
      const toHtml = (val) =>
        val && !/<p>|<\/p>|<br\s*\/?>|<strong>|<\/strong>|<em>|<\/em>|<u>|<\/u>|<s>|<\/s>|<ol>|<ul>|<li>|<span/i.test(val)
        ? val.split(/\r\n|\r|\n/).map(line => `<p>${line || "<br>"}</p>`).join("")
        : val;
      if (data.isApplicableNote) data.isApplicableNote = toHtml(data.isApplicableNote);
      setState((state) => ({ ...state, withholdingTax: data }));
    }
  }

  useEffect(() => {
    const stateId = selectedState || match.params.state;
    if (stateId) getWitholdingTaxByState(stateId);
  }, [selectedState]);

  async function saveChanges() {
    if (!state.submitting) {
      setState((state) => ({ ...state, submitting: true }));
      const result = await postRequest("/tax/editWithholdingTax", {
        ...state.withholdingTax,
        stateId: selectedState,
        files: await Promise.all(
          state.files.map(async (f) => ({
            base64File: await readFileAsBase64(f),
            fileName: f.name
          }))
        ),
        filesToDelete: state.filesToDelete
      });
      if (result.success) {
        logEvent(null, "Edited", "Withholding Tax");
        setTimeout(() => history.goBack(), 1000);
      }
    }
  }

  return (
    <div className={css({ width: 605, paddingBottom: 34 })}>
      <Header style={{ fontSize: 24, marginBottom: 41 }} as="h3">
        Withholding Tax
      </Header>
      <Form>
        <Grid>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field>
                <label>Is it applicable?</label>
                <Checkbox
                  style={{ marginRight: 20 }}
                  label="Yes"
                  checked={state.withholdingTax.isApplicable}
                  size="large"
                  onChange={() =>
                    setState((state) => ({
                      ...state,
                      withholdingTax: {
                        ...state.withholdingTax,
                        isApplicable: !state.withholdingTax.isApplicable
                      }
                    }))
                  }
                />
                <Checkbox
                  label="No"
                  checked={
                    state.withholdingTax.isApplicable === false ? true : false
                  }
                  size="large"
                  onChange={() =>
                    setState((state) => ({
                      ...state,
                      withholdingTax: {
                        ...state.withholdingTax,
                        isApplicable:
                          state.withholdingTax.isApplicable === false
                            ? true
                            : false
                      }
                    }))
                  }
                />
              </Form.Field>
              <Form.Field>
                <label>Note</label>
                <RichTextEditor
                  value={state.withholdingTax.isApplicableNote || ""}
                  onChange={(value) =>
                    setState((state) => ({
                      ...state,
                      withholdingTax: {
                        ...state.withholdingTax,
                        isApplicableNote: value
                      }
                    }))
                  }
                />
              </Form.Field>
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column>
              <Form.Field required>
                <FileUpload
                  fileList={state.files}
                  setFileToApp={(files) =>
                    setState((state) => ({
                      ...state,
                      files: [...state.files, ...files]
                    }))
                  }
                  deleteFileToApp={(fileName) =>
                    setState((state) => ({
                      ...state,
                      files: [...state.files.filter((f) => f.name !== fileName)]
                    }))
                  }
                  multiple={true}
                />
              </Form.Field>
              {state.withholdingTax.withholdingTaxFiles &&
                state.withholdingTax.withholdingTaxFiles.map((f) => (
                  <p
                    key={shortid.generate()}
                    onClick={() =>
                      setState((state) => ({
                        ...state,
                        filesToDelete: [...state.filesToDelete, f.id],
                        withholdingTax: {
                          ...state.withholdingTax,
                          withholdingTaxFiles: [
                            ...state.withholdingTax.withholdingTaxFiles.filter(
                              (wtf) => wtf.id !== f.id
                            )
                          ]
                        }
                      }))
                    }
                    className={css({
                      "&:hover": { color: "#707898" },
                      cursor: "pointer"
                    })}
                  >
                    <i
                      className="fal fa-trash-alt"
                      style={{ marginRight: 8 }}
                    />
                    {f.fileName}
                  </p>
                ))}
            </Grid.Column>
          </Grid.Row>
          <Grid.Row columns={1}>
            <Grid.Column
              floated="right"
              className={css({ textAlign: "right" })}
            >
              <Button
                size="large"
                className="cancelButton"
                onClick={() => history.goBack()}
                data-automation="cancel"
              >
                Cancel
              </Button>
              <Button
                size="large"
                className="saveButton"
                onClick={() => saveChanges()}
                data-automation="save"
              >
                {state.submitting ? "Submitting..." : "Save"}
              </Button>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </div>
  );
}

export default withRouter(EditWithholdingTax);
